import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type {
	PbjPredictionJobCoverageGap,
	PbjPredictionManualHoursCreateBody,
	PbjPredictionManualHoursEntry,
	PbjPredictionManualHoursUpdateBody,
} from "../../../utils/models";
import { invalidatePbjPredictionJobCoverage, PBJ_PREDICTION_JOB_COVERAGE_QUERY_KEY, pbjCachedQueryOptions } from "./pbjQueryOptions";
import { PBJ_MODEL_QUERY_KEY } from "./usePbjModel";

// Four scopes get invalidated on every prediction-manual-hours write:
//   1) the list itself (so the manage-dialog table refreshes), and
//   2) the prediction (`[PBJ_MODEL_QUERY_KEY, "prediction"]`) — the BE adds these
//      hours into the HPPD buckets, so the stars / banner / category breakdown
//      all change with each write. Without the prediction invalidation the FE
//      would render stale stars until the next manual refetch.
//   3) the tracked plan's progress — the tracker now sums these entries too, so a write
//      moves its hour levels and its score. Its query is keyed on the department alone
//      (never on this dialog's window), so no other invalidation would ever retire it, and
//      the dialog is reachable from the tracker tab itself. Note what a write does NOT do:
//      an entry spanning the whole quarter lands in the pre-plan baseline as well as the
//      elapsed window and cancels out of `delivered`, so the bar can legitimately hold
//      still. Only an entry dated from the plan's start onward reads as progress.
//   4) the missing-job warning — an entry that names a job is exactly what retires that job's gap,
//      so leaving this stale would keep telling the user to add hours they just added.
const invalidatePredictionScopes = (queryClient: ReturnType<typeof useQueryClient>) => {
	queryClient.invalidateQueries({
		queryKey: [PBJ_MODEL_QUERY_KEY, "prediction-manual-hours"],
	});
	queryClient.invalidateQueries({
		queryKey: [PBJ_MODEL_QUERY_KEY, "prediction"],
	});
	queryClient.invalidateQueries({
		queryKey: [PBJ_MODEL_QUERY_KEY, "tracked-plan"],
	});
	invalidatePbjPredictionJobCoverage(queryClient);
};

/**
 * PBJ-relevant jobs with no hours in the prediction over the committed window.
 *
 * Separate from `usePbjPredictionManualHours` even though both key off the same window: this answer
 * changes only when shifts or entries change, so it survives every Calculate / target / CMI edit
 * that re-runs the prediction.
 */
/**
 * Jobs with no hours in the prediction window.
 *
 * `enabled` carries a correctness condition, not just an optimisation. The endpoint answers "which
 * InHouse jobs have no SCHEDULED hours", and that is only the same question as "which jobs are
 * missing from your score" while the score is computed from the schedule. A department scored from
 * its PBJ audit hours (DAT-597) is scored from payroll punches, which carry a CMS job code and no
 * InHouse job at all — so the endpoint would warn that a DON has no hours while the DON's punches
 * are in fact scored, or stay silent about a job genuinely absent from payroll. Neither answer is
 * about the numbers on screen, so the caller passes `enabled: false` there and shows nothing.
 */
export const usePbjPredictionJobCoverage = (departmentId: string, startDate: string, endDate: string, enabled: boolean) => {
	return useQuery<PbjPredictionJobCoverageGap[]>({
		queryKey: [...PBJ_PREDICTION_JOB_COVERAGE_QUERY_KEY, departmentId, startDate, endDate],
		queryFn: async () => {
			const res = await backendService.getPbjPredictionJobCoverage(departmentId, startDate, endDate);
			return res.data;
		},
		enabled: enabled && !!departmentId && !!startDate && !!endDate,
		...pbjCachedQueryOptions,
	});
};

export const usePbjPredictionManualHours = (departmentId: string, startDate: string, endDate: string) => {
	return useQuery<PbjPredictionManualHoursEntry[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "prediction-manual-hours", departmentId, startDate, endDate],
		queryFn: async () => {
			const res = await backendService.getPbjPredictionManualHours(departmentId, startDate, endDate);
			return res.data;
		},
		enabled: !!departmentId && !!startDate && !!endDate,
		// The list is kept current by invalidatePredictionScopes on every create/update/delete
		// (invalidation refetches active observers regardless of staleTime) — never auto-refetch.
		...pbjCachedQueryOptions,
	});
};

export const useCreatePbjPredictionManualHours = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	return useMutation<PbjPredictionManualHoursEntry, Error, PbjPredictionManualHoursCreateBody>({
		mutationFn: async (body) => {
			const res = await backendService.createPbjPredictionManualHours(body);
			return res.data;
		},
		onSuccess: (entry) => {
			invalidatePredictionScopes(queryClient);
			showSnackbar(t("pbj_model.prediction.manual_hours.create_success"), "success");
			track("pbj_prediction_manual_hours_create", {
				department_id: entry.departmentId,
				job_id: entry.jobId,
				hours_mode: entry.hoursMode,
				is_rn: entry.isRn,
				weekday_hours: entry.weekdayHours,
				weekend_hours: entry.weekendHours,
				period_days: Math.max(0, DateTime.fromISO(entry.periodEndDate).diff(DateTime.fromISO(entry.periodStartDate), "days").days + 1),
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.prediction.manual_hours.create_failed"), "error");
			trackError("pbj_prediction_manual_hours_create", {
				error_message: error?.message,
			});
		},
	});
};

type UpdateVars = { entryId: string; body: PbjPredictionManualHoursUpdateBody };

export const useUpdatePbjPredictionManualHours = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	return useMutation<PbjPredictionManualHoursEntry, Error, UpdateVars>({
		mutationFn: async ({ entryId, body }) => {
			const res = await backendService.updatePbjPredictionManualHours(entryId, body);
			return res.data;
		},
		onSuccess: (entry, vars) => {
			invalidatePredictionScopes(queryClient);
			showSnackbar(t("pbj_model.prediction.manual_hours.update_success"), "success");
			track("pbj_prediction_manual_hours_update", {
				department_id: entry.departmentId,
				entry_id: vars.entryId,
				job_id: entry.jobId,
				hours_mode: entry.hoursMode,
				is_rn: entry.isRn,
				weekday_hours: entry.weekdayHours,
				weekend_hours: entry.weekendHours,
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.prediction.manual_hours.update_failed"), "error");
			trackError("pbj_prediction_manual_hours_update", {
				error_message: error?.message,
			});
		},
	});
};

type DeleteVars = { entryId: string; departmentId: string };

export const useDeletePbjPredictionManualHours = () => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	return useMutation<void, Error, DeleteVars>({
		mutationFn: async ({ entryId, departmentId }) => {
			await backendService.deletePbjPredictionManualHours(entryId, departmentId);
		},
		onSuccess: (_data, vars) => {
			invalidatePredictionScopes(queryClient);
			showSnackbar(t("pbj_model.prediction.manual_hours.delete_success"), "success");
			track("pbj_prediction_manual_hours_delete", {
				department_id: vars.departmentId,
				entry_id: vars.entryId,
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.prediction.manual_hours.delete_failed"), "error");
			trackError("pbj_prediction_manual_hours_delete", {
				error_message: error?.message,
			});
		},
	});
};
